import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  BarChart3, 
  Download, 
  TrendingUp, 
  TrendingDown,
  FileSpreadsheet,
  FileText
} from "lucide-react";
import { formatCurrency, formatPercent } from "@/lib/utils";
import { API } from "@/lib/api";


export default function Reports() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/reports/margins`);
      setReport(res.data);
    } catch (error) {
      toast.error("Erro ao carregar relatório");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    window.open(`${API}/export/report-excel`, '_blank');
    toast.success("Exportação Excel iniciada");
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Relatório de Margens
          </h1>
          <p className="text-slate-500 mt-1">Análise de lucratividade por produto/serviço</p>
        </div>
        <div className="flex gap-2 no-print">
          <Button 
            variant="outline" 
            onClick={handleExportExcel}
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Exportar Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={handlePrint}
            data-testid="print-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Imprimir PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="stat-card" data-testid="summary-total">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total de Itens</p>
                  <p className="text-2xl font-bold font-mono text-slate-900">
                    {report.summary.total_items}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="summary-profitable">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Com Lucro</p>
                  <p className="text-2xl font-bold font-mono text-emerald-600">
                    {report.summary.profitable_items}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="summary-unprofitable">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-50 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Com Prejuízo</p>
                  <p className="text-2xl font-bold font-mono text-rose-600">
                    {report.summary.unprofitable_items}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="summary-avg-margin">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  report.summary.avg_margin_percent >= 0 ? 'bg-emerald-50' : 'bg-rose-50'
                }`}>
                  {report.summary.avg_margin_percent >= 0 ? (
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-rose-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Margem Média</p>
                  <p className={`text-2xl font-bold font-mono ${
                    report.summary.avg_margin_percent >= 0 ? 'text-emerald-600' : 'text-rose-600'
                  }`}>
                    {formatPercent(report.summary.avg_margin_percent)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Report Table */}
      <Card>
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
            Detalhamento por Item
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!report || report.items.length === 0 ? (
            <div className="empty-state py-12">
              <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhum dado para exibir</p>
              <p className="text-sm text-slate-400 mt-2">
                Cadastre fichas de custo para ver o relatório
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Preço Venda</TableHead>
                  <TableHead className="text-right">Custo Total</TableHead>
                  <TableHead className="text-right">Margem (R$)</TableHead>
                  <TableHead className="text-right">Margem (%)</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.items.map((item) => (
                  <TableRow key={item.id} data-testid={`report-row-${item.id}`}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-xs">
                        {item.service_type === 'servico' ? 'Serviço' : 'Produto'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(item.sale_price)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(item.total_cost)}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-medium ${
                      item.is_profitable ? 'text-emerald-600' : 'text-rose-600'
                    }`}>
                      {formatCurrency(item.contribution_margin)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono font-medium px-2 py-1 rounded-md text-sm ${
                        item.is_profitable 
                          ? 'bg-emerald-50 text-emerald-700' 
                          : 'bg-rose-50 text-rose-700'
                      }`}>
                        {formatPercent(item.contribution_margin_percent)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {item.is_profitable ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                          <TrendingUp className="w-3 h-3" />
                          Lucro
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-50 text-rose-700 text-xs font-medium">
                          <TrendingDown className="w-3 h-3" />
                          Prejuízo
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Print-specific content */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          .stat-card { box-shadow: none; border: 1px solid #e2e8f0; }
        }
      `}</style>
    </div>
  );
}
